import { useQuery } from "@tanstack/react-query";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Card,
  EmptyState,
  Pill,
  StatCard,
} from "../../components/design-system/index.js";
import { getCapitalGainsPreview } from "../../lib/api-modules/tax.js";
import { usePreferences } from "../../lib/preferences.js";

function formatCents(cents: number, hide: boolean): string {
  if (hide) return "••••";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function TaxCapitalGainsPage() {
  const { privacyMode } = usePreferences();
  const hide = privacyMode === true;
  const preview = useQuery({
    queryKey: ["capital-gains-preview"],
    queryFn: () => getCapitalGainsPreview(),
  });

  return (
    <TaxLayout
      title="Capital gains preview"
      subtitle="Realized year-to-date + unrealized × LT/ST. Wash-sale candidates flagged across all accounts (per Rev Rul 2008-5)."
    >
      {preview.isPending ? (
        <Card className="p-6 text-sm text-[var(--color-muted)]">Loading…</Card>
      ) : preview.isError ? (
        <Card className="p-6 text-sm text-[var(--color-danger)]">
          Failed to load preview.
        </Card>
      ) : preview.data ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label="Realized LT"
              value={formatCents(preview.data.realized_lt_cents, hide)}
              sub={`${preview.data.as_of}`}
            />
            <StatCard
              label="Realized ST"
              value={formatCents(preview.data.realized_st_cents, hide)}
            />
            <StatCard
              label="Unrealized LT"
              value={formatCents(preview.data.unrealized_lt_cents, hide)}
            />
            <StatCard
              label="Unrealized ST"
              value={formatCents(preview.data.unrealized_st_cents, hide)}
            />
          </div>

          {preview.data.wash_sale_candidates.length > 0 && (
            <Card className="p-5 border border-[var(--color-warning)] bg-[var(--color-warning-soft)]">
              <div className="text-sm font-semibold mb-2 text-[var(--color-text)]">
                Wash-sale candidates ({preview.data.wash_sale_candidates.length})
              </div>
              <div className="text-xs text-[var(--color-muted)] mb-3">
                Per IRS Reg 1.1091-1 + Rev Rul 2008-5: a loss is disallowed if substantially identical stock was acquired within 30 days before/after the sale, including in IRAs / Roth IRAs / HSAs.
              </div>
              <div className="space-y-2">
                {preview.data.wash_sale_candidates.map((c, i) => (
                  <div
                    key={`${c.sale_tx_id}-${c.replacement.lot_id}-${i}`}
                    className="text-sm grid grid-cols-1 sm:grid-cols-3 gap-2"
                  >
                    <div>
                      <div className="font-medium text-[var(--color-text)]">{c.ticker}</div>
                      <div className="text-xs text-[var(--color-muted)]">
                        Loss {formatCents(c.sale.realized_loss_cents, hide)} on {c.sale.sale_date}
                      </div>
                    </div>
                    <div className="text-xs text-[var(--color-muted)]">
                      Replacement {c.replacement.acquired_date} ({c.replacement.account_type ?? "—"})
                    </div>
                    <Pill tone={c.scope === "cross_account" ? "warning" : "neutral"}>
                      {c.scope === "cross_account" ? "Cross-account" : "Same account"} · {c.days_apart}d apart
                    </Pill>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {preview.data.by_account.length > 0 && (
            <Card className="p-5">
              <div className="text-sm font-semibold mb-3 text-[var(--color-text)]">
                By account
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-[var(--color-muted)] text-left">
                  <tr>
                    <th className="py-1">Account</th>
                    <th className="py-1 text-right">Realized LT</th>
                    <th className="py-1 text-right">Realized ST</th>
                    <th className="py-1 text-right">Unrealized LT</th>
                    <th className="py-1 text-right">Unrealized ST</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.by_account.map((a) => (
                    <tr key={a.account_id} className="border-t border-[var(--color-border-subtle)]">
                      <td className="py-1.5 font-mono text-xs">
                        {a.account_type ?? "—"}
                      </td>
                      <td className="py-1.5 text-right font-variant-tabular">
                        {formatCents(a.realized_lt_cents, hide)}
                      </td>
                      <td className="py-1.5 text-right font-variant-tabular">
                        {formatCents(a.realized_st_cents, hide)}
                      </td>
                      <td className="py-1.5 text-right font-variant-tabular">
                        {formatCents(a.unrealized_lt_cents, hide)}
                      </td>
                      <td className="py-1.5 text-right font-variant-tabular">
                        {formatCents(a.unrealized_st_cents, hide)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {preview.data.by_lot.length === 0 && preview.data.wash_sale_candidates.length === 0 && (
            <EmptyState
              title="No lots to preview"
              description="Once you import or add account positions, they'll appear here with their LT/ST classification."
            />
          )}
        </>
      ) : null}
    </TaxLayout>
  );
}
